import {
    EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    getDomain,
    normalizeDataLayer,
    normalizeDiacriticsString,
    TagDataLayerPartial,
} from '@axa-fr-axion/tag';
import { ProfilClient } from '@axa-fr-axion/user-journey-services';
import { JourneyData } from 'data';
import { GenderOptions } from 'features/contact/identity/gender';
import { NavigationPath, StepNavigationPath } from 'interfaces';
import { NAVIGATION_DATA } from './Tag.data';
import { TagCivility } from './Tag.interface';
import hashSha, { hashShaWithoutCode } from 'utils/hashSha';
import { Preset } from 'presets';

export const EXCLUDED_KEY_TO_NORMALIZE: Partial<keyof TagDataLayerPartial>[] = [
    ...EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    'code_du_produit',
];
export const KEEPING_SPACE_KEY_TO_NORMALIZE: Partial<keyof TagDataLayerPartial>[] = [
    ...EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    'form_contact_prenom_hash',
    'form_contact_nom_hash',
];

export const getHashedDataLayer = async (data: JourneyData): Promise<TagDataLayerPartial> => {
    const fiscalJourneyDataLayer = journeyDataToDataLayer(data);
    const normalizedDataLayer = normalizeDataLayer(
        fiscalJourneyDataLayer,
        EXCLUDED_KEY_TO_NORMALIZE,
        KEEPING_SPACE_KEY_TO_NORMALIZE
    );
    return hashDataLayer(normalizedDataLayer, data);
};

const journeyDataToDataLayer = (data: JourneyData): TagDataLayerPartial => {
    const {
        identity,
        homeAddress,
        folder,
        logementType,
        logementResidencePrincipale,
        locataireType,
        logementNombrePieces,
        logementDebutAssurance,
        logementAdresse,
    } = data;

    return {
        statut_client: toProfileClientConversion(folder.personne?.profilClient) as Lowercase<ProfilClient>,
        form_contact_ville: homeAddress.city,
        form_contact_codepostal: homeAddress.postalCode,
        form_contact_codepostal_hash: homeAddress.postalCode,
        form_contact_civilite: toTagCivility(identity.gender),
        form_contact_nom_hash: identity.lastname,
        form_contact_prenom_hash: identity.firstname,
        form_contact_telephone_hash: identity.phone,
        form_contact_email_hash: identity.email?.toLowerCase(),
        nom_domaine_email: getDomain(identity.email?.toLowerCase()),
        type_hors_cible: '',
        form_numero_dossier_hash: folder.lead?.hash,
        lead_canal_traitement: '',
        type_bien: logementType?.toString(),
        residence_principale: logementResidencePrincipale?.toString(),
        qualite_assure: locataireType?.toString(),
        nombre_pieces: logementNombrePieces?.toString(),
        date_debut_contrat: logementDebutAssurance?.toString(),
        form_bien_ville: logementAdresse?.city?.toLowerCase(),
        form_bien_codepostal: logementAdresse?.postalCode,
    };
};

export const shouldSendTagEventNavigation = (preset: Preset, step: NavigationPath): step is StepNavigationPath =>
    Boolean(NAVIGATION_DATA(preset)[ step as StepNavigationPath ]);

export const hashDataLayer = async (
    data: TagDataLayerPartial,
    journeyData: JourneyData
): Promise<TagDataLayerPartial> => {
    const lastname = await hashSha(normalizeDiacriticsString(journeyData.identity.lastname));
    const firstname = await hashSha(data.form_contact_prenom_hash);
    const zipcodeContact = await hashSha(data.form_contact_codepostal);
    const hashnpc = (await hashShaWithoutCode(lastname + firstname + zipcodeContact))?.toUpperCase();
    const formBienVilleHash = await hashSha(journeyData.logementAdresse?.city);
    const formBienCodePostalHash = await hashSha(journeyData.logementAdresse?.postalCode);
    const formContactDatenaissanceHash = await hashSha(journeyData.identity.birthdate);

    return {
        ...data,
        form_contact_nom_hash: lastname,
        form_contact_prenom_hash: firstname,
        form_contact_codepostal_hash: zipcodeContact,
        form_entreprise_codepostal_hash: await hashSha(data.form_entreprise_codepostal as string),
        hashnpc,
        form_contact_telephone_hash: await hashSha(data.form_contact_telephone_hash),
        form_contact_email_hash: await hashSha(data.form_contact_email_hash),
        form_contact_email_hash_2: await hashShaWithoutCode(data.form_contact_email_hash),
        form_contact_datenaissance_hash: formContactDatenaissanceHash,
        // The folder id is already hashed by the backend
        form_numero_dossier_hash: data.form_numero_dossier_hash,
        form_bien_ville_hash: formBienVilleHash,
        form_bien_codepostal_hash: formBienCodePostalHash,
    };
};

export const toTagCivility = (gender?: GenderOptions): TagCivility | '' => {
    switch (gender) {
        case 'ms':
            return 'madame';
        case 'mr':
            return 'monsieur';
        default:
            return '';
    }
};

export const toProfileClientConversion = (profil?: ProfilClient) => {
    if (profil === 'UnbeknownstClient') {
        return 'Client';
    } else {
        return profil;
    }
};
